import { useState, useEffect } from "react";
import { MRT_RowData, MRT_TableInstance } from "material-react-table";
import {
  Badge,
  IconButton,
  ListItem,
  Menu,
  SORT_QUERY_NAME,
  ASCENDING_NAME,
  DESCENDING_NAME,
} from "@techzyapp/ui-lib";
import { useSearchParams } from "react-router-dom";

interface TableSortProps<T extends MRT_RowData> {
  table: MRT_TableInstance<T>;
}

const TableSort = <T extends MRT_RowData>({ table }: TableSortProps<T>) => {
  const { getHeaderGroups, setSorting } = table;
  const [activeSort, setActiveSort] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const sort = searchParams.get(SORT_QUERY_NAME);

    if (sort) {
      const [column, direction] = sort.split(
        new RegExp(`(${ASCENDING_NAME}|${DESCENDING_NAME})$`)
      );
      const sortData = direction
        ? [
            {
              id: column,
              desc: direction === DESCENDING_NAME,
            },
          ]
        : [];
      setSorting(sortData);
      setActiveSort(sort);
    }
  }, [searchParams, setSorting]);

  const SortOptions = getHeaderGroups().flatMap((headerGroup) =>
    headerGroup.headers
      .filter((header) => header.column.columnDef.enableSorting)
      .map((header) => ({
        id: header.column.columnDef.id,
        header: header.column.columnDef.header,
      }))
  );

  const handleSort = (sortOption: string, desc: boolean | null) => {
    if (sortOption === "") {
      setSorting([]);
      setActiveSort(null);
    } else {
      setSorting([{ id: sortOption, desc: !!desc }]);
      setActiveSort(`${sortOption}${desc ? DESCENDING_NAME : ASCENDING_NAME}`);
    }
  };

  return SortOptions.length ? (
    <Menu
      component={
        <Badge variant="dot" invisible={!activeSort} color="medium">
          <IconButton iconName="Sort" variant="filled" />
        </Badge>
      }
      items={[
        {
          children: <ListItem disablePadding>None</ListItem>,
          onClick: () => handleSort("", null),
          selected: activeSort === null,
        },
        ...SortOptions.flatMap((option) => [
          {
            children: (
              <ListItem key={`${option.id}-${ASCENDING_NAME}`} disablePadding>
                {`${option.header} (${ASCENDING_NAME})`}
              </ListItem>
            ),
            onClick: () => handleSort(option.id, false),
            selected: activeSort === `${option.id}${ASCENDING_NAME}`,
          },
          {
            children: (
              <ListItem key={`${option.id}-${DESCENDING_NAME}`} disablePadding>
                {`${option.header} (${DESCENDING_NAME})`}
              </ListItem>
            ),
            onClick: () => handleSort(option.id, true),
            selected: activeSort === `${option.id}${DESCENDING_NAME}`,
          },
        ]),
      ]}
    />
  ) : null;
};

export default TableSort;
